import { useRef, useContext, useState, useEffect } from "react";
import "../../../Components/Resume-Upload/ResumeUpload.css";
import UploadIcon from "../../../Assets/Images/upload.svg";
import { DynamicContext } from "../../../Context/DynamicContext";
import CancelIcon from "@mui/icons-material/Cancel";
import FileIcon from "../../../Common/Common-Function/IconMapping";
import { displayFileName } from "../../../Common/Common-Function/CommonFunctions";
import Button from "../../../Common/Button/Button";
import {
  VYLABS_BASE_URL,
  CREATE_RUN_SET_ID,
  PARSED_RESUME_URL,
  JOBS_PROCESS_JD,
  JOBS_PROCESS_EXCEL,
  WEB_SOCKET_URL,
} from "../../../helper/url_helper";
import { post } from "../../../helper/Api_helper";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import JobXLResults from "./JobXLResults.js";
import JobJDResults from "./JobJDResults.js";

function JobFileUpload({ JdOrXLImport, defaultClientId }) {
  const { isDarkMode, pingIntervalRef } = useContext(DynamicContext);
  const [uploadedJD, setUploadedJD] = useState({ files: [] });
  const [, setRunSetId] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [jobResults, setJobResults] = useState([]);
  const [failedJobs, setFailedJobs] = useState([]);
  const [incompleteJobs, setIncompleteJobs] = useState([]);
  const [resultUploadedFiles, setResultUploadedFiles] = useState([]);
  const uploadedFiles = uploadedJD.files;
  const uploadedFileNames = uploadedFiles.map((file) => file.name);

  const fileRef = useRef(null);
  const socketRef = useRef(null);
  const pingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const importInProgressRef = useRef(false);
  const importRequestInProgressRef = useRef(false);

  const RECONNECT_ATTEMPT_TOTAL_COUNT = 5;
  const BASE_RECONNECT_DELAY = 1000;
  const PING_INTERVAL = 30000;
  const PING_TIMEOUT = 10000;
  const MAX_FILES = 10;

  const isJDImport = JdOrXLImport === "JD";
  const allowedExtensions = isJDImport
    ? [".pdf", ".doc", ".docx", ".txt",".jpeg",".jpg",".png",".svg"]
    : [".xlsx", ".csv"];
  const allowedExtensionsLabel = isJDImport
    ? "PDF, DOC, DOCX, TXT, JPEG, JPG, PNG, and SVG"
    : "XLSX and CSV";

  useEffect(() => {
    closeWebSocket();
    setIsImporting(false);
    setShowResults(false);
    setUploadedJD({ files: [] });
    setRunSetId(null);
    setJobResults([]);
    setFailedJobs([]);
    setIncompleteJobs([]);
    setResultUploadedFiles([]);
    if (fileRef.current) fileRef.current.value = "";
  }, [JdOrXLImport]);

  const clearPingTimers = () => {
    if (pingIntervalRef?.current) {
      console.log("Clearing WebSocket ping interval");
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pingTimeoutRef.current) {
      console.log("Clearing WebSocket ping timeout");
      clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = null;
    }
  };

  const startPingInterval = (ws) => {
    console.log("Starting WebSocket ping interval...");
    clearPingTimers();

    const sendPing = () => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.warn(
          "Skipping WebSocket ping. Connection is not open:",
          ws.readyState,
        );
        return;
      }

      try {
        console.warn("Sending ping");
        ws.send("ping");

        if (pingTimeoutRef.current) {
          clearTimeout(pingTimeoutRef.current);
        }

        pingTimeoutRef.current = setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            console.log("No pong received within timeout, reconnecting...");
            ws.close(4000, "Ping timeout");
          }
        }, PING_TIMEOUT);
      } catch (error) {
        console.error("Error sending WebSocket ping:", error);
      }
    };

    if (ws.readyState === WebSocket.OPEN) {
      console.warn("Sending initial ping");
      sendPing();
    } else {
      console.log(
        "WebSocket is not open yet. Initial ping will be sent after connection.",
      );
    }

    pingIntervalRef.current = setInterval(sendPing, PING_INTERVAL);
  };

  const handleReconnect = () => {
    if (!importInProgressRef.current) {
      console.log("WebSocket reconnect skipped. No import is currently active.");
      return;
    }

    if (reconnectAttemptRef.current >= RECONNECT_ATTEMPT_TOTAL_COUNT) {
      console.error(
        "Maximum WebSocket reconnection attempts reached:",
        reconnectAttemptRef.current,
      );
      Toast(
        "error",
        "WebSocket connection lost. Import processing may continue in the background.",
      );
      return;
    }

    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current) +
        Math.random() * 1000,
      30000,
    );

    console.log(
      `WebSocket reconnect attempt ${reconnectAttemptRef.current + 1} scheduled in ${Math.round(delay)}ms`,
    );

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptRef.current += 1;
      console.log(
        `Attempting WebSocket reconnection ${reconnectAttemptRef.current}/${RECONNECT_ATTEMPT_TOTAL_COUNT}...`,
      );
      initializeWebSocket();
    }, delay);
  };

  const getExcelJobRecordKey = (record) => {
    if (!record || typeof record !== "object") return "";

    let preview = record.job_preview;
    if (typeof preview === "string") {
      try {
        preview = JSON.parse(preview);
      } catch {
        preview = {};
      }
    }
    preview = preview && typeof preview === "object" ? preview : {};

    const stablePreview = JSON.stringify(
      Object.keys(preview)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = preview[key];
          return sorted;
        }, {}),
    );

    const title =
      record.job_title || preview.title || preview.job_title || "";
    const client = preview.client_name || record.client_name || "";
    const location = preview.location || record.location || "";

    return [
      record.name || record.file_name || record.filename || "",
      title,
      client,
      location,
      stablePreview,
    ].join("::");
  };

  const getImportRecordKey = (record, isExcelEvent) => {
    if (isExcelEvent) {
      return getExcelJobRecordKey(record);
    }
    return (
      record.object_key ||
      record.id ||
      record.event_id ||
      `${record.name || "unknown"}-${record.event_type || ""}`
    );
  };

  const initializeWebSocket = () => {
    if (
      socketRef.current &&
      (socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      console.log(
        "WebSocket connection already exists. Current readyState:",
        socketRef.current.readyState,
      );
      return socketRef.current;
    }

    const token = localStorage.getItem("token");
    const wsProtocol =
      window.location.protocol === "https:" ? "wss://" : "wss://";
    const wsUrl = `${wsProtocol}${WEB_SOCKET_URL}/ws?token=${encodeURIComponent(
      "Bearer " + (token || ""),
    )}`;

    try {
      console.log("Creating new WebSocket connection...");
      console.log("WebSocket URL:", `${wsProtocol}${WEB_SOCKET_URL}/ws`);
      console.log("WebSocket readyState before connection: CONNECTING");

      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connection established");
        console.log("WebSocket readyState:", ws.readyState);
        reconnectAttemptRef.current = 0;
        startPingInterval(ws);
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") {
          console.warn("Pong received");
          if (pingTimeoutRef.current) {
            clearTimeout(pingTimeoutRef.current);
            pingTimeoutRef.current = null;
          }
          return;
        }

        try {
          console.log("Raw WebSocket message:", event.data);

          if (typeof event.data !== "string") {
            console.warn("Ignoring non-string job WebSocket message:", event.data);
            return;
          }

          const separatorIndex = event.data.indexOf("|");
          if (separatorIndex === -1) {
            console.warn("Job WebSocket message does not contain a payload separator.");
            return;
          }

          const eventLabel = event.data.slice(0, separatorIndex);
          const rawPayload = event.data.slice(separatorIndex + 1);
          const eventType = eventLabel.split(":")[0].toLowerCase();
          const eventId = eventLabel.includes(":")
            ? eventLabel.slice(eventLabel.indexOf(":") + 1)
            : "";

          let payload;
          try {
            payload = JSON.parse(rawPayload);
          } catch (parseError) {
            console.error("Unable to parse job WebSocket payload:", parseError, rawPayload);
            return;
          }

          if (!payload || typeof payload !== "object") return;

          const statusFromEvent = eventType.includes("incomplete")
            ? "incomplete"
            : eventType.includes("-err") || eventType.includes("error")
              ? "failed"
              : eventType.includes("updated")
                ? "updated"
                : eventType.includes("created")
                  ? "created"
                  : "";

          const resultRecord = {
            ...payload,
            event_type: eventType,
            event_id: eventId,
            ...(statusFromEvent && !payload.job_creation_status
              ? { job_creation_status: statusFromEvent }
              : {}),
          };

          const isJobEvent =
            eventType.includes("process-job-jd-") ||
            eventType.includes("process-job-excel-") ||
            eventType.includes("process-job-xl-");

          if (!isJobEvent) {
            console.log("WebSocket message is not a job import result event:", eventType);
            return;
          }

          const isExcelEvent =
            eventType.includes("process-job-excel-") ||
            eventType.includes("process-job-xl-");

          setJobResults((previous) => {
            const current = Array.isArray(previous) ? previous : [];
            const recordKey = getImportRecordKey(resultRecord, isExcelEvent);

            const existingIndex = current.findIndex(
              (item) => getImportRecordKey(item, isExcelEvent) === recordKey,
            );

            if (existingIndex === -1) return [...current, resultRecord];

            const next = [...current];
            next[existingIndex] = { ...next[existingIndex], ...resultRecord };
            return next;
          });

          if (statusFromEvent === "failed" || resultRecord.metadata?.error) {
            setFailedJobs((previous) => {
              const current = Array.isArray(previous) ? previous : [];
              const key = isExcelEvent
                ? getExcelJobRecordKey(resultRecord)
                : (resultRecord.object_key || resultRecord.id || resultRecord.name);
              const index = current.findIndex((item) => {
                const itemKey = isExcelEvent
                  ? getExcelJobRecordKey(item)
                  : (item.object_key || item.id || item.name);
                return itemKey === key;
              });
              if (index === -1) return [...current, resultRecord];
              const next = [...current];
              next[index] = { ...next[index], ...resultRecord };
              return next;
            });
          }

          if (statusFromEvent === "incomplete") {
            setIncompleteJobs((previous) => {
              const current = Array.isArray(previous) ? previous : [];
              const key = isExcelEvent
                ? getExcelJobRecordKey(resultRecord)
                : (resultRecord.object_key || resultRecord.id || resultRecord.name);
              const index = current.findIndex((item) => {
                const itemKey = isExcelEvent
                  ? getExcelJobRecordKey(item)
                  : (item.object_key || item.id || item.name);
                return itemKey === key;
              });
              if (index === -1) return [...current, resultRecord];
              const next = [...current];
              next[index] = { ...next[index], ...resultRecord };
              return next;
            });
          }

          console.log("Parsed job import result event:", {
            eventType,
            eventId,
            fileName: resultRecord.name,
            status: statusFromEvent || resultRecord.job_creation_status || "processing",
            resultRecord,
          });
        } catch (error) {
          console.error("Error processing job WebSocket message:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        console.error("WebSocket readyState at error:", ws.readyState);
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed", event);
        console.log("WebSocket close code:", event.code);
        console.log("WebSocket close reason:", event.reason);
        console.log("WebSocket was cleanly closed:", event.wasClean);

        clearPingTimers();
        if (socketRef.current === ws) socketRef.current = null;

        if (event.code === 1006 || (event.code !== 1000 && importInProgressRef.current)) {
          console.log(
            `Connection closed with code ${event.code}, attempting to reconnect...`,
          );
        }

        handleReconnect();
      };

      return ws;
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      handleReconnect();
      return null;
    }
  };

  const closeWebSocket = () => {
    console.log("Closing job import WebSocket connection...");
    importInProgressRef.current = false;

    if (reconnectTimeoutRef.current) {
      console.log("Clearing pending WebSocket reconnect timeout");
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    clearPingTimers();

    if (socketRef.current) {
      const ws = socketRef.current;
      socketRef.current = null;

      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        console.log("Closing WebSocket manually. Current readyState:", ws.readyState);
        ws.close(1000, "Job import component cleanup");
      } else {
        console.log(
          "WebSocket is already closed. Current readyState:",
          ws.readyState,
        );
      }
    } else {
      console.log("No active WebSocket connection to close.");
    }
  };

  useEffect(() => () => closeWebSocket(), []);

  const addSelectedFiles = (incomingFiles) => {
    if (isImporting) {
      Toast("error", "Please wait until the current import is finished.");
      return;
    }

    const files = Array.from(incomingFiles || []);
    if (!files.length) return;

    const invalidFiles = files.filter((file) => {
      const extension = `.${file.name.split(".").pop().toLowerCase()}`;
      return !allowedExtensions.includes(extension);
    });

    if (invalidFiles.length) {
      Toast("error", `Only ${allowedExtensionsLabel} files are accepted.`);
      return;
    }

    setUploadedJD((previous) => {
      const existingNames = new Set(previous.files.map((file) => file.name));
      const incomingNames = new Set();
      const duplicates = files.filter((file) => {
        if (existingNames.has(file.name) || incomingNames.has(file.name)) {
          return true;
        }
        incomingNames.add(file.name);
        return false;
      });
      const uniqueFiles = files.filter((file) => !duplicates.includes(file));
      const availableSlots = Math.max(0, MAX_FILES - previous.files.length);
      const acceptedFiles = uniqueFiles.slice(0, availableSlots);

      if (duplicates.length) {
        Toast("error", "Duplicate files are not accepted.");
      }
      if (uniqueFiles.length > availableSlots) {
        Toast("error", `You can upload a maximum of ${MAX_FILES} files.`);
      }

      return { files: [...previous.files, ...acceptedFiles] };
    });

    if (fileRef.current) fileRef.current.value = "";
  };

  const handleJDUploadInputChange = (event) => {
    addSelectedFiles(event.target.files);
  };

  const handleFileDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isImporting) setIsDragging(true);
  };

  const handleFileDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    addSelectedFiles(event.dataTransfer.files);
  };

  const handleFileRemove = (index) => {
    if (isImporting) return;
    setUploadedJD((previous) => ({
      files: previous.files.filter((_, fileIndex) => fileIndex !== index),
    }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClearFiles = () => {
    if (isImporting) return;
    setUploadedJD({ files: [] });
    setRunSetId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const createRunSetId = async () => {
    const response = await post(VYLABS_BASE_URL, CREATE_RUN_SET_ID, {
      folderName: uploadedFileNames.join(","),
      totalFiles: uploadedFileNames.length,
    });

    const payload = response?.data ?? response;
    const createdId =
      payload?.runSetID ??
      payload?.run_set_id ??
      payload?.runSetId ??
      payload?.id ??
      payload;

    if (!createdId) {
      throw new Error("The server did not return a run set ID.");
    }

    setRunSetId(createdId);
    return createdId;
  };

  const getUploadURLs = async (fileNames) => {
    const response = await post(VYLABS_BASE_URL, PARSED_RESUME_URL, {
      FileNames: fileNames,
    });

    const payload = response?.data ?? response;
    const urls =
      payload?.PresignedUploadURLs ??
      payload?.presignedUploadURLs ??
      payload?.presigned_upload_urls ??
      payload?.uploadUrls ??
      payload;

    if (!Array.isArray(urls) || urls.length !== fileNames.length) {
      throw new Error("Could not get a valid upload URL for every selected file.");
    }

    return urls.map((item) =>
      typeof item === "string"
        ? item
        : item?.url || item?.presigned_url || item?.upload_url,
    );
  };

  const uploadFilesToStorage = async (files, presignedUrls) => {
    const uploadResults = await Promise.all(
      files.map(async (file, index) => {
        const uploadUrl = presignedUrls[index];
        if (!uploadUrl) {
          throw new Error(`Missing upload URL for ${file.name}.`);
        }

        const response = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": "application/octet-stream" },
        });

        if (!response.ok) {
          throw new Error(
            `Upload failed for ${file.name} (${response.status}).`,
          );
        }
        return file.name;
      }),
    );

    return uploadResults;
  };

  const handleProcessJD = async (id, fileNames) => {
    let payload;
    if(defaultClientId) {
      payload= {
        fileNames,
        runSetID: id,
        default_client_id: defaultClientId,
      };
    }
    else {
      payload={
        fileNames,
        runSetID: id,
      }
    }
    return post(VYLABS_BASE_URL, JOBS_PROCESS_JD, payload);
  };

  const handleProcessXL = async (id, fileNames) => {
    let payload;
    if(defaultClientId) {
      payload= {
        fileNames,
        runSetID: id,
        default_client_id: defaultClientId,
      };
    }
    else {
      payload={
        fileNames,
        runSetID: id,
      }
    }
    return post(VYLABS_BASE_URL, JOBS_PROCESS_EXCEL, payload);
  };

  const handleImport = async () => {
    if (isImporting || importRequestInProgressRef.current) return;
    if (!uploadedFiles.length) {
      Toast("error", `Please upload ${isJDImport ? "a job description" : "a spreadsheet"} file.`);
      return;
    }

    setIsImporting(true);
    importRequestInProgressRef.current = true;
    importInProgressRef.current = true;

    try {
      // Start listening before processing is requested so early server events are not missed.
      console.log("Initializing WebSocket before starting job import...");
      initializeWebSocket();

      const filesToUpload = [...uploadedFiles];
      const fileNames = filesToUpload.map((file) => file.name);
      setResultUploadedFiles(filesToUpload);
      setJobResults([]);
      setFailedJobs([]);
      setIncompleteJobs([]);
      const createdRunSetId = await createRunSetId();
      const presignedUrls = await getUploadURLs(fileNames);

      await uploadFilesToStorage(filesToUpload, presignedUrls);

      const processResponse = isJDImport
        ? await handleProcessJD(createdRunSetId, fileNames)
        : await handleProcessXL(createdRunSetId, fileNames);

      const processRunId =
        processResponse?.run_id ??
        processResponse?.runId ??
        processResponse?.runID ??
        processResponse?.id ??
        null;

      Toast(
        "success",
        `${isJDImport ? "Job description" : "Bulk spreadsheet"} upload completed. Processing has started.`,
      );

      console.log("Job import started:", {
        type: JdOrXLImport,
        runSetId: createdRunSetId,
        processRunId,
        response: processResponse,
      });

      // Display the matching results component while WebSocket events continue
      // to populate its data.
      setShowResults(true);
    } catch (error) {
      console.error("Error importing job files:", error);
      Toast("error", error?.message || "Unable to import files. Please try again.");
      closeWebSocket();
    } finally {
      setIsImporting(false);
      importRequestInProgressRef.current = false;
    }
  };

  const resetJobState = () => {
    closeWebSocket();
    setShowResults(false);
    setUploadedJD({ files: [] });
    setRunSetId(null);
    setJobResults([]);
    setFailedJobs([]);
    setIncompleteJobs([]);
    setResultUploadedFiles([]);
    setIsImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  if (showResults) {
    const commonResultProps = {
      jobResults,
      setJobResults,
      uploadedFiles: resultUploadedFiles,
      setUploadedFiles: setResultUploadedFiles,
      failedJobs,
      setFailedJobs,
      incompleteJobs,
      setIncompleteJobs,
      showHeader: true,
      resetJobState,
    };

    return isJDImport ? (
      <JobJDResults {...commonResultProps} />
    ) : (
      <JobXLResults {...commonResultProps} />
    );
  }

  const uploadedFilesNameList = uploadedFiles.length > 0 && (
    <div
      className="uploaded-files-name-list"
      style={{
        background: isDarkMode ? "rgba(30, 30, 40, 0.7)" : "white",
        borderRadius: "16px",
        padding: "20px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)",
        marginBottom: "20px",
        border: "1px solid var(--border__color)",
        marginTop: "-10px",
      }}
    >
      <div className="uploaded-files-name-list-container">
        <h3
          style={{
            color: "#6860EE",
            borderBottom: "1px solid var(--border__color)",
            paddingBottom: "16px",
            marginTop: "4px",
            marginBottom: "12px",
            fontSize: "18px",
            fontWeight: "600",
          }}
        >
          {JdOrXLImport === "JD" ? "Job Descriptions" : "Spreadsheets"}{" "}
          <span className="resume-length-span">{uploadedFiles.length}</span>
        </h3>

        <div
          className="file-and-folder-container"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            maxHeight: "250px",
            overflowY: "auto",
            padding: "5px",
          }}
        >
          {uploadedFiles.map((file, index) => (
            <div
              className="file-container"
              key={`${file.name}-${file.lastModified}-${index}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                background: isDarkMode
                  ? "rgba(34, 108, 226, 0.1)"
                  : "rgba(34, 108, 226, 0.05)",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                border: "1px solid rgba(34, 108, 226, 0.1)",
              }}
            >
              <span
                className="file-name"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "15px",
                  fontWeight: "500",
                  color: isDarkMode ? "white" : "#333",
                  minWidth: 0,
                }}
              >
                <FileIcon fileName={file.name} />
                {displayFileName(file.name)}
              </span>

              <CancelIcon
                sx={{
                  cursor: "pointer",
                  color: isDarkMode ? "rgba(255, 255, 255, 0.6)" : "#666",
                  fontSize: "20px",
                  flexShrink: 0,
                }}
                onClick={() => handleFileRemove(index)}
              />
            </div>
          ))}
        </div>
      </div>

      <div
        className="modal-buttons"
        style={{
          display: "flex",
          gap: "15px",
          justifyContent: "flex-end",
          marginTop: "20px",
        }}
      >
        <Button
          label="Clear"
          backgroundColor={isDarkMode ? "rgba(255, 255, 255, 0.1)" : "#F5F4F3"}
          color={isDarkMode ? "white" : "#7B7A7A"}
          border={
            isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.2)"
              : "1px solid #ACACAC"
          }
          event={handleClearFiles}
          disabled={isImporting}
          style={{
            borderRadius: "8px",
            padding: "8px 20px",
            fontWeight: "500",
            transition: "all 0.2s ease",
          }}
        />
        <Button
          label={isImporting ? "Importing..." : "Import"}
          backgroundColor="#6860EE"
          event={handleImport}
          disabled={isImporting}
          style={{
            borderRadius: "8px",
            padding: "8px 20px",
            fontWeight: "500",
            boxShadow: "0 4px 12px rgba(34, 108, 226, 0.25)",
            transition: "all 0.2s ease",
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="resume-upload-container">
      <div className="upload-resume-container">
        <div className="outer-main-container">
          {JdOrXLImport === "JD" ? (
            <div className="outer-container">
              <div
                className="page-intro"
                style={{
                  textAlign: "center",
                  maxWidth: "800px",
                  margin: "0 auto 30px",
                  padding: "0 20px",
                }}
              >
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    lineHeight: "1.6",
                    color: isDarkMode
                      ? "rgba(255, 255, 255, 0.7)"
                      : "rgba(0, 0, 0, 0.6)",
                    marginBottom: "10px",
                  }}
                >
                  Upload Job Descriptions to parse and automatically extract
                  relevant information. Our system will process them to create
                  jobs.
                </p>

                <div
                  className="upload-steps"
                  style={{
                    width: "100%",
                    margin: "30px 0",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "0",
                  }}
                >
                  {["Upload JD", "System extracts data", "Jobs created"].map(
                    (step, index) => (
                      <div
                        key={step}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flex: index === 2 ? "0 1 auto" : "1",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "10px",
                            minWidth: "120px",
                          }}
                        >
                          <div
                            style={{
                              width: "34px",
                              height: "34px",
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              color: "#fff",
                              background: "#6860EE",
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </div>
                          <span
                            style={{
                              color: "#6860EE",
                              fontSize: "14px",
                              fontWeight: 500,
                              textAlign: "center",
                            }}
                          >
                            {step}
                          </span>
                        </div>
                        {index < 2 && (
                          <div
                            style={{
                              flex: 1,
                              minWidth: "35px",
                              borderTop: "1px dashed #6860EE",
                              margin: "0 10px 28px",
                            }}
                          />
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div
                className={`file-upload-outer ${isDragging ? "active" : ""}`}
              >
                {uploadedFilesNameList}
                <div className="Files-upload-container">
                  <div
                    className={`file-drop-container ${isDragging ? "drag-over" : ""}`}
                    onDragOver={handleFileDragOver}
                    onDragLeave={handleFileDragLeave}
                    onDrop={handleFileDrop}
                    data-is-dragging={isDragging}
                    onClick={() => !isImporting && fileRef.current?.click()}
                    style={{
                      background: isDarkMode
                        ? "rgba(20, 20, 30, 0.7)"
                        : "rgba(255, 255, 255, 0.9)",
                      borderRadius: "16px",
                      backdropFilter: "blur(10px)",
                      padding: "40px 20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                      transform: isDragging ? "scale(1.02)" : "scale(1)",
                      border: isDragging
                        ? "2px dashed #4138e057"
                        : isDarkMode
                          ? "2px dashed rgba(255, 255, 255, 0.3)"
                          : "2px dashed rgba(34, 108, 226, 0.3)",
                      minHeight: "280px",
                      margin: "2vw auto",
                      marginTop: uploadedFiles.length > 0 ? "115px" : "2vw",
                      cursor: isImporting ? "not-allowed" : "pointer",
                    }}
                  >
                    <label
                      htmlFor="job-files-upload"
                      className="file-upload-label"
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        cursor: isImporting ? "not-allowed" : "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          borderRadius: "50%",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isDarkMode
                            ? "rgba(104, 96, 238, 0.1)"
                            : "rgba(104, 96, 238, 0.05)",
                        }}
                      >
                        <img
                          src={UploadIcon}
                          alt={
                            isJDImport ? "Upload Job Description" : "Upload Spreadsheet"
                          }
                          style={{
                            cursor: isImporting ? "not-allowed" : "pointer",
                            width: "40px",
                            height: "40px",
                            color: isDarkMode ? "#ffffff" : "#6860EE",
                          }}
                        />
                      </div>
                      <p
                        className="drag-drop"
                        style={{
                          cursor: isImporting ? "not-allowed" : "pointer",
                          fontSize: "18px",
                          fontWeight: "500",
                          color: isDarkMode ? "#ffffff" : "#6860EE",
                          margin: "10px 0 5px",
                        }}
                      >
                        {isJDImport
                          ? "Drag & Drop or Click to upload the Job Descriptions"
                          : "Drag & Drop or Click to upload spreadsheet"}
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          color: isDarkMode
                            ? "rgba(255, 255, 255, 0.6)"
                            : "rgba(0, 0, 0, 0.6)",
                          marginTop: "5px",
                          textAlign: "center",
                        }}
                      >
                        {isJDImport
                          ? "PDF, DOC, DOCX, and TXT files. Single or multiple files supported."
                          : "XLSX, CSV files. Single or multiple files supported."}
                      </p>
                      <input
                        id="job-files-upload"
                        type="file"
                        ref={fileRef}
                        onChange={handleJDUploadInputChange}
                        style={{ display: "none" }}
                        multiple
                        disabled={isImporting}
                        accept={
                          isJDImport
                            ? ".pdf,.doc,.docx,.txt,.jpeg,.jpg,.png,.svg"
                            : ".xlsx,.csv"
                        }
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="outer-container">
              <div
                className="page-intro"
                style={{
                  textAlign: "center",
                  maxWidth: "800px",
                  margin: "0 auto 30px",
                  padding: "0 20px",
                }}
              >
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    lineHeight: "1.6",
                    color: isDarkMode
                      ? "rgba(255, 255, 255, 0.7)"
                      : "rgba(0, 0, 0, 0.6)",
                    marginBottom: "10px",
                  }}
                >
                  Upload spreadsheets to parse and automatically extract
                  relevant information. Our system will process them to create
                  jobs.
                </p>

                <div
                  className="upload-steps"
                  style={{
                    width: "100%",
                    margin: "30px 0",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "0",
                  }}
                >
                  {[
                    "Upload Spreadsheet",
                    "System extracts data",
                    "Jobs created",
                  ].map((step, index) => (
                    <div
                      key={step}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flex: index === 2 ? "0 1 auto" : "1",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "10px",
                          minWidth: "120px",
                        }}
                      >
                        <div
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            color: "#fff",
                            background: "#6860EE",
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </div>
                        <span
                          style={{
                            color: "#6860EE",
                            fontSize: "14px",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        >
                          {step}
                        </span>
                      </div>
                      {index < 2 && (
                        <div
                          style={{
                            flex: 1,
                            minWidth: "35px",
                            borderTop: "1px dashed #6860EE",
                            margin: "0 10px 28px",
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div
                className={`file-upload-outer ${isDragging ? "active" : ""}`}
              >
                {uploadedFilesNameList}
                <div className="Files-upload-container">
                  <div
                    className={`file-drop-container ${isDragging ? "drag-over" : ""}`}
                    onDragOver={handleFileDragOver}
                    onDragLeave={handleFileDragLeave}
                    onDrop={handleFileDrop}
                    data-is-dragging={isDragging}
                    onClick={() => !isImporting && fileRef.current?.click()}
                    style={{
                      background: isDarkMode
                        ? "rgba(20, 20, 30, 0.7)"
                        : "rgba(255, 255, 255, 0.9)",
                      borderRadius: "16px",
                      backdropFilter: "blur(10px)",
                      padding: "40px 20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                      transform: isDragging ? "scale(1.02)" : "scale(1)",
                      border: isDragging
                        ? "2px dashed #4138e057"
                        : isDarkMode
                          ? "2px dashed rgba(255, 255, 255, 0.3)"
                          : "2px dashed rgba(34, 108, 226, 0.3)",
                      minHeight: "280px",
                      margin: "2vw auto",
                      marginTop: uploadedFiles.length > 0 ? "115px" : "2vw",
                      cursor: isImporting ? "not-allowed" : "pointer",
                    }}
                  >
                    <label
                      htmlFor="job-files-upload"
                      className="file-upload-label"
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        cursor: isImporting ? "not-allowed" : "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          borderRadius: "50%",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isDarkMode
                            ? "rgba(104, 96, 238, 0.1)"
                            : "rgba(104, 96, 238, 0.05)",
                        }}
                      >
                        <img
                          src={UploadIcon}
                          alt={
                            isJDImport ? "Upload Job Description" : "Upload Spreadsheet"
                          }
                          style={{
                            cursor: isImporting ? "not-allowed" : "pointer",
                            width: "40px",
                            height: "40px",
                            color: isDarkMode ? "#ffffff" : "#6860EE",
                          }}
                        />
                      </div>
                      <p
                        className="drag-drop"
                        style={{
                          cursor: isImporting ? "not-allowed" : "pointer",
                          fontSize: "18px",
                          fontWeight: "500",
                          color: isDarkMode ? "#ffffff" : "#6860EE",
                          margin: "10px 0 5px",
                        }}
                      >
                        {isJDImport
                          ? "Drag & Drop or Click to upload the Job Descriptions"
                          : "Drag & Drop or Click to upload spreadsheet"}
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          color: isDarkMode
                            ? "rgba(255, 255, 255, 0.6)"
                            : "rgba(0, 0, 0, 0.6)",
                          marginTop: "5px",
                          textAlign: "center",
                        }}
                      >
                        {isJDImport
                          ? "PDF, DOC, DOCX, and TXT files. Single or multiple files supported."
                          : "XLSX, CSV files. Single or multiple files supported."}
                      </p>
                      <input
                        id="job-files-upload"
                        type="file"
                        ref={fileRef}
                        onChange={handleJDUploadInputChange}
                        style={{ display: "none" }}
                        multiple
                        disabled={isImporting}
                        accept={
                          isJDImport
                            ? ".pdf,.doc,.docx,.txt,.jpeg,.jpg,.png,.svg"
                            : ".xlsx,.csv"
                        }
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default JobFileUpload;